import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import {
  renderSocialDesignSvg,
  type SocialDesign,
} from "../../../../../data/socialDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "../svgTextureRaster";

/**
 * Design-store render strategy for the Sociales (SOCIALS) widget — the social
 * sibling of the Crawl/Clock design renderers. When the widget's
 * `options.designId` resolves to a {@link SocialDesign}, this renders that
 * design's CARD SVG (from the local catalog, with the streamer @handle baked in)
 * as a single Pixi sprite stretched to the widget bounds.
 *
 * Unlike the clock/crawl designs the social card is STATIC (no per-frame
 * update), and the SOCIALS widget is `cacheAsTexture`'d by the factory — so
 * `render()` is async and AWAITS its first (and only) rasterisation, with the
 * fonts loaded first, so the cached frame already carries the real card.
 *
 * One instance per widget (the parent `SocialsWidgetRenderer` keeps a Map keyed
 * by widget id), so multiple social bars with different designs coexist and each
 * can free its own GPU texture on teardown.
 */
export class SocialDesignRenderer {
  private container: Container | null = null;

  /** Texture currently shown — destroyed on teardown. */
  private texture: Texture | null = null;

  private destroyed = false;

  constructor(private readonly design: SocialDesign) {}

  async render(
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
    handle: string,
  ): Promise<void> {
    if (this.destroyed) {
      return;
    }

    const destWidth = Math.max(1, destination.width);
    const destHeight = Math.max(1, destination.height);

    this.container = new Container();
    parentContainer.addChild(this.container);

    const scaleForBounds = Math.max(
      1,
      destWidth / this.design.cardWidth,
      destHeight / this.design.cardHeight,
    );
    const renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = destWidth;
    sprite.height = destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);

    try {
      // Load the design's Google fonts BEFORE the single raster so the cached
      // frame is drawn with the real typefaces (not a fallback).
      await ensureDesignFontsReady(this.design.fonts);
      if (this.destroyed || sprite.destroyed) {
        return;
      }

      const svg = renderSocialDesignSvg(this.design, handle);
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        renderScale,
      );

      if (this.destroyed || sprite.destroyed) {
        texture.destroy(true);
        return;
      }

      this.texture = texture;
      sprite.texture = texture;
      sprite.width = destWidth;
      sprite.height = destHeight;
    } catch {
      // Leave the empty sprite in place; a re-render will retry.
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
